import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  assessmentAggregations,
  assessmentDirections,
  assessmentValueTypes,
  type DefinitionView,
  type DepartmentView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  aggregationKeys,
  canSubmitDefinition,
  definitionFormOf,
  definitionRequestBody,
  definitionVersionBody,
  directionKeys,
  emptyDefinitionForm,
  localizedText,
  valueTypeKeys,
  wholeClub,
  withValueType,
  type DefinitionForm,
} from './assessments';

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

export interface ProtocolsSectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  departments: readonly DepartmentView[];
  definitions: readonly DefinitionView[] | null;
  onChanged: () => Promise<void>;
}

/**
 * Assessment protocols. A protocol version never changes once created; editing one saves a new
 * version, so sessions that used the earlier version keep exactly what they used, and a changed
 * distance, course, surface or method starts a new comparable series.
 */
export function ProtocolsSection({
  locale,
  base,
  csrfToken,
  departments,
  definitions,
  onChanged,
}: ProtocolsSectionProps): React.JSX.Element {
  const [form, setForm] = useState<DefinitionForm>(() =>
    emptyDefinitionForm(departments[0]?.id ?? ''),
  );
  const [editing, setEditing] = useState<DefinitionView | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);

  const departmentChoice =
    form.departmentId === '' ? (departments[0]?.id ?? wholeClub) : form.departmentId;
  const measured = form.valueType === 'numeric' || form.valueType === 'duration';
  const update = (changes: Partial<DefinitionForm>): void => {
    setForm({ ...form, ...changes });
    setSaved(false);
  };

  async function submit(): Promise<void> {
    setErrorKey(null);
    try {
      if (editing === null) {
        await apiRequest<DefinitionView>(`${base}/assessment-definitions`, {
          method: 'POST',
          body: definitionRequestBody({ ...form, departmentId: departmentChoice }, locale),
          csrfToken,
        });
      } else {
        // A new version carries the content only; the department and key of a protocol never change.
        const content = definitionVersionBody(form, locale, {
          titles: editing.titles,
          instructions: editing.instructions,
          rubricLevels: editing.rubricLevels,
        });
        await apiRequest<DefinitionView>(`${base}/assessment-definitions/${editing.id}/versions`, {
          method: 'POST',
          body: content,
          csrfToken,
        });
      }
      setEditing(null);
      setForm(emptyDefinitionForm(departments[0]?.id ?? ''));
      setSaved(true);
      await onChanged();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box component="section" aria-label={translate(locale, 'assessments.protocols.title')}>
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'assessments.protocols.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'assessments.saved')}
        </Alert>
      )}
      {definitions?.length === 0 && (
        <Typography color="text.secondary">
          {translate(locale, 'assessments.protocols.empty')}
        </Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {definitions?.map((definition) => (
          <Box
            component="li"
            key={definition.id}
            sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Typography sx={{ fontWeight: 500 }}>
              {localizedText(definition.titles, locale)}
            </Typography>
            <Chip size="small" label={translate(locale, valueTypeKeys[definition.valueType])} />
            <Chip
              size="small"
              variant="outlined"
              label={`${translate(locale, 'assessments.protocol.version')} ${String(definition.versionNumber)}`}
            />
            {[definition.protocol.distance, definition.protocol.surface]
              .filter((part): part is string => part !== null)
              .map((part) => (
                <Typography key={part} variant="body2" color="text.secondary">
                  {part}
                </Typography>
              ))}
            <Button
              size="small"
              onClick={() => {
                setEditing(definition);
                setForm(definitionFormOf(definition, locale));
                setSaved(false);
              }}
            >
              {translate(locale, 'assessments.protocols.edit')}
            </Button>
          </Box>
        ))}
      </Stack>
      <Box
        component="form"
        sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {editing === null && (
          <>
            <TextField
              select
              size="small"
              sx={{ minWidth: 180 }}
              label={translate(locale, 'assessments.protocol.department')}
              value={departmentChoice}
              onChange={(event) => {
                update({ departmentId: event.target.value });
              }}
            >
              <MenuItem value={wholeClub}>
                {translate(locale, 'assessments.protocol.wholeClub')}
              </MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              required
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
              label={translate(locale, 'assessments.protocol.code')}
              value={form.code}
              onChange={(event) => {
                update({ code: event.target.value });
              }}
            />
          </>
        )}
        <TextField
          size="small"
          required
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
          label={translate(locale, 'assessments.protocol.title')}
          value={form.title}
          onChange={(event) => {
            update({ title: event.target.value });
          }}
        />
        <TextField
          select
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 200 } }}
          label={translate(locale, 'assessments.protocol.valueType')}
          value={form.valueType}
          onChange={(event) => {
            const chosen = assessmentValueTypes.find((type) => type === event.target.value);
            if (chosen !== undefined) {
              setForm(withValueType(form, chosen));
              setSaved(false);
            }
          }}
        >
          {assessmentValueTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {translate(locale, valueTypeKeys[type])}
            </MenuItem>
          ))}
        </TextField>
        {form.valueType === 'numeric' && (
          <TextField
            size="small"
            required
            sx={{ width: 140 }}
            label={translate(locale, 'assessments.protocol.unit')}
            value={form.unit}
            onChange={(event) => {
              update({ unit: event.target.value });
            }}
          />
        )}
        {measured && (
          <>
            <TextField
              size="small"
              sx={{ width: 130 }}
              label={translate(locale, 'assessments.protocol.precision')}
              value={form.precision}
              onChange={(event) => {
                update({ precision: event.target.value });
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            />
            <TextField
              size="small"
              sx={{ width: 170 }}
              label={translate(locale, 'assessments.protocol.minimum')}
              value={form.minimum}
              onChange={(event) => {
                update({ minimum: event.target.value });
              }}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              size="small"
              sx={{ width: 170 }}
              label={translate(locale, 'assessments.protocol.maximum')}
              value={form.maximum}
              onChange={(event) => {
                update({ maximum: event.target.value });
              }}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField
              select
              size="small"
              sx={{ minWidth: 180 }}
              label={translate(locale, 'assessments.protocol.direction')}
              value={form.direction}
              onChange={(event) => {
                const chosen = assessmentDirections.find(
                  (direction) => direction === event.target.value,
                );
                if (chosen !== undefined) update({ direction: chosen });
              }}
            >
              {assessmentDirections.map((direction) => (
                <MenuItem key={direction} value={direction}>
                  {translate(locale, directionKeys[direction])}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
        <TextField
          size="small"
          sx={{ width: 150 }}
          label={translate(locale, 'assessments.protocol.attempts')}
          value={form.attemptsAllowed}
          onChange={(event) => {
            update({ attemptsAllowed: event.target.value });
          }}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
        />
        <TextField
          select
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 240 } }}
          label={translate(locale, 'assessments.protocol.aggregation')}
          value={form.aggregation}
          onChange={(event) => {
            const chosen = assessmentAggregations.find(
              (aggregation) => aggregation === event.target.value,
            );
            if (chosen !== undefined) update({ aggregation: chosen });
          }}
        >
          {assessmentAggregations
            .filter((aggregation) => measured || aggregation !== 'mean')
            .map((aggregation) => (
              <MenuItem key={aggregation} value={aggregation}>
                {translate(locale, aggregationKeys[aggregation])}
              </MenuItem>
            ))}
        </TextField>
        {measured && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.allowOutOfRange}
                  onChange={(event) => {
                    update({ allowOutOfRange: event.target.checked });
                  }}
                />
              }
              label={translate(locale, 'assessments.protocol.allowOutOfRange')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.ratioScale}
                  onChange={(event) => {
                    update({ ratioScale: event.target.checked });
                  }}
                />
              }
              label={translate(locale, 'assessments.protocol.ratioScale')}
            />
          </Box>
        )}
        {form.valueType === 'rubric' && (
          <Stack spacing={1} sx={{ width: '100%' }}>
            {form.rubricLevels.map((description, index) => (
              <TextField
                // The levels are an ordered list that is only appended to or trimmed at the end.
                key={`level-${String(index)}`}
                size="small"
                required
                label={`${translate(locale, 'assessments.protocol.rubricLevel')} ${String(index + 1)}`}
                value={description}
                onChange={(event) => {
                  update({
                    rubricLevels: form.rubricLevels.map((level, position) =>
                      position === index ? event.target.value : level,
                    ),
                  });
                }}
              />
            ))}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                disabled={form.rubricLevels.length >= 10}
                onClick={() => {
                  update({ rubricLevels: [...form.rubricLevels, ''] });
                }}
              >
                {translate(locale, 'assessments.protocol.addLevel')}
              </Button>
              <Button
                size="small"
                disabled={form.rubricLevels.length <= 2}
                onClick={() => {
                  update({ rubricLevels: form.rubricLevels.slice(0, -1) });
                }}
              >
                {translate(locale, 'assessments.protocol.removeLevel')}
              </Button>
            </Box>
          </Stack>
        )}
        {(
          [
            ['distance', 'assessments.protocol.distance'],
            ['course', 'assessments.protocol.course'],
            ['surface', 'assessments.protocol.surface'],
            ['method', 'assessments.protocol.method'],
          ] as const
        ).map(([field, labelKey]) => (
          <TextField
            key={field}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
            label={translate(locale, labelKey)}
            value={form[field]}
            onChange={(event) => {
              update({ [field]: event.target.value });
            }}
          />
        ))}
        <TextField
          size="small"
          multiline
          sx={{ minWidth: { xs: '100%', sm: 300 }, flexGrow: 1 }}
          label={translate(locale, 'assessments.protocol.instructions')}
          value={form.instructions}
          onChange={(event) => {
            update({ instructions: event.target.value });
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
          {translate(locale, 'assessments.protocol.contextHint')}
        </Typography>
        <Button
          type="submit"
          variant="contained"
          sx={{ minHeight: 44 }}
          disabled={!canSubmitDefinition(form, editing === null)}
        >
          {translate(
            locale,
            editing === null ? 'assessments.protocols.add' : 'assessments.protocols.newVersion',
          )}
        </Button>
        {editing !== null && (
          <Button
            sx={{ minHeight: 44 }}
            onClick={() => {
              setEditing(null);
              setForm(emptyDefinitionForm(departments[0]?.id ?? ''));
            }}
          >
            {translate(locale, 'assessments.protocols.cancel')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
